import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"

/**
 * Syncs a tab component's active tab with a URL query param.
 * Returns a wrapped change handler that updates the URL on tab click,
 * and calls `onChange` when the URL param changes (e.g. browser back/fwd).
 */
export function useUrlTabSync(
  tabs: { id: string }[],
  urlParam: string | undefined,
  onChange: (id: string) => void,
  replaceUrl: boolean
): (id: string) => void {
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (!urlParam) return
    const param = searchParams.get(urlParam)
    if (param && tabs.some(t => t.id === param)) {
      onChange(param)
    }
    // tabs and onChange are intentionally omitted: they are stable in practice
    // (tabs = static array, onChange = setState), and including them would
    // cause spurious syncs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParam, searchParams])

  return (id: string) => {
    if (urlParam) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.set(urlParam, id)
          return next
        },
        { replace: replaceUrl }
      )
    }
    onChange(id)
  }
}
